// Story 1.32 — TanStack Query mutation:
// POST /api/v1/admin/financial-tools/trigger-reconciliation.
//
// Returns 202 ACCEPTED with `status: "queued_pending_story_6_7"` —
// the actual reconciliation worker ships in Epic 6 Story 6.7. The
// button + audit row + admin-log email are wired now so when the
// worker lands, no UI change is needed.

import { useMutation } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type TriggerReconciliationInput = {
  justification: string;
  idempotency_key: string;
};

export type TriggerReconciliationResponse = {
  data: {
    status: "queued_pending_story_6_7";
    recorded_at: string;
    note: string;
  };
};

export class TriggerReconciliationError extends Error {
  code?: string;
  field?: string;

  constructor(message: string) {
    super(message);
    this.name = "TriggerReconciliationError";
  }
}

async function triggerReconciliation(
  input: TriggerReconciliationInput,
): Promise<TriggerReconciliationResponse> {
  const { idempotency_key, ...body } = input;
  const response = await fetch(
    "/api/v1/admin/financial-tools/trigger-reconciliation",
    {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-idempotency-key": idempotency_key,
      },
      body: JSON.stringify(body),
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Trigger reconciliation failed (HTTP ${response.status})`;
    let parsed: Record<string, unknown> | undefined;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
      parsed = raw?.error;
    } catch {
      // body not JSON
    }
    const err = new TriggerReconciliationError(message);
    err.code = parsed?.code as string | undefined;
    err.field = parsed?.field as string | undefined;
    throw err;
  }
  return (await response.json()) as TriggerReconciliationResponse;
}

export function useExecuteTriggerReconciliation() {
  return useMutation({
    mutationFn: triggerReconciliation,
  });
}
